import type { EntityIntakeDefaults } from './entityFormationDefaults';
import {
  TIMELINE_OPTIONS,
  REVENUE_EXPECTATIONS_OPTIONS,
  GROWTH_OUTLOOK_OPTIONS,
  INDUSTRY_OPTIONS,
  STRATEGIC_INTENT_OPTIONS,
  NUMBER_OF_OWNERS_OPTIONS,
  MANAGEMENT_PREFERENCE_OPTIONS,
  YES_NO_NOT_SURE_OPTIONS,
  YES_NO_OPTIONS,
  YES_NO_LATER_OPTIONS,
  LIABILITY_RISK_OPTIONS,
  RISK_FACTOR_OPTIONS,
  INITIAL_FUNDING_OPTIONS,
  ADDITIONAL_SERVICES_OPTIONS,
} from './entityFormationOptions';

/**
 * Entity Formation Intake Form Field Keys
 * All field keys for the business entity formation intake form.
 */
export const ENTITY_INTAKE_FIELD_KEYS = [
  // Section 1: Client Information
  'full_legal_name',
  'preferred_name',
  'phone_number',
  'email_address',
  'street_address',
  'street_address_2',
  'city',
  'state',
  'zip_code',
  'state_of_residence',
  'us_citizen',
  'citizenship_status',
  'ssn_itin',
  
  // Section 2: Business Overview
  'legal_business_name',
  'alternate_business_names',
  'business_description',
  'industry',
  'industry_other',
  
  // Section 3: Business Goals & Vision
  'timeline',
  'revenue_expectations',
  'growth_outlook',
  'strategic_intent',
  'strategic_intent_other',
  
  // Section 4: Ownership & Management
  'number_of_owners',
  'owners',
  'management_preference',
  'future_ownership_changes',
  
  // Section 5: Location & Operations
  'operating_states',
  'operates_outside_formation_state',
  'has_employees',
  'uses_contractors',
  'sells_online',
  'serves_nationally',
  
  // Section 6: Risk & Compliance
  'risk_factors',
  'liability_risk',
  
  // Section 7: Banking, Tax & Financial Setup
  'has_business_bank_account',
  'uses_bookkeeping_software',
  'bookkeeping_software_name',
  'discussed_tax_planning',
  
  // Section 8: Branding & Digital Presence
  'owns_domain',
  'domain_name',
  'website_timeline',
  'interested_in_brand_protection',
  
  // Section 9: Capital & Growth Planning
  'initial_funding',
  'initial_funding_other',
  'plans_to_raise_capital',
  
  // Section 10: Additional Services Interest
  'additional_services',
  
  // Section 11: Acknowledgments
  'acknowledge_not_attorney_or_cpa',
  'acknowledge_entity_formation_scope',
  'certify_accuracy',
  'client_signature',
  'signature_date',
] as const;

export type EntityIntakeFieldKey = typeof ENTITY_INTAKE_FIELD_KEYS[number];

/**
 * Entity Formation Intake Form Sections
 * Defines the structure of the intake form with steps, titles, field keys, and option sets.
 */
export const ENTITY_INTAKE_SECTIONS = [
  {
    step: 1,
    title: 'Client Information',
    fieldKeys: [
      'full_legal_name',
      'preferred_name',
      'phone_number',
      'email_address',
      'street_address',
      'street_address_2',
      'city',
      'state',
      'zip_code',
      'state_of_residence',
      'us_citizen',
      'citizenship_status',
      'ssn_itin',
    ] as const,
    // Note: state and state_of_residence use US_STATES from usStates.ts
    conditionals: {
      citizenship_status: (data: EntityIntakeDefaults) => data.us_citizen === 'no',
    },
  },
  {
    step: 2,
    title: 'Business Overview',
    fieldKeys: [
      'legal_business_name',
      'alternate_business_names',
      'business_description',
      'industry',
      'industry_other',
    ] as const,
    options: {
      industry: INDUSTRY_OPTIONS,
    },
    conditionals: {
      industry_other: (data: EntityIntakeDefaults) => Array.isArray(data.industry) && data.industry.includes('other'),
    },
  },
  {
    step: 3,
    title: 'Business Goals & Vision',
    fieldKeys: [
      'timeline',
      'revenue_expectations',
      'growth_outlook',
      'strategic_intent',
      'strategic_intent_other',
    ] as const,
    options: {
      timeline: TIMELINE_OPTIONS,
      revenue_expectations: REVENUE_EXPECTATIONS_OPTIONS,
      growth_outlook: GROWTH_OUTLOOK_OPTIONS,
      strategic_intent: STRATEGIC_INTENT_OPTIONS,
    },
    conditionals: {
      strategic_intent_other: (data: EntityIntakeDefaults) => Array.isArray(data.strategic_intent) && data.strategic_intent.includes('other'),
    },
  },
  {
    step: 4,
    title: 'Ownership & Management',
    fieldKeys: [
      'number_of_owners',
      'owners',
      'management_preference',
      'future_ownership_changes',
    ] as const,
    options: {
      number_of_owners: NUMBER_OF_OWNERS_OPTIONS,
      management_preference: MANAGEMENT_PREFERENCE_OPTIONS,
      future_ownership_changes: YES_NO_NOT_SURE_OPTIONS,
    },
  },
  {
    step: 5,
    title: 'Location & Operations',
    fieldKeys: [
      'operating_states',
      'operates_outside_formation_state',
      'has_employees',
      'uses_contractors',
      'sells_online',
      'serves_nationally',
    ] as const,
    options: {
      operates_outside_formation_state: YES_NO_NOT_SURE_OPTIONS,
      has_employees: YES_NO_OPTIONS,
      uses_contractors: YES_NO_OPTIONS,
      sells_online: YES_NO_OPTIONS,
      serves_nationally: YES_NO_OPTIONS,
      // Note: operating_states uses US_STATES from usStates.ts
    },
  },
  {
    step: 6,
    title: 'Risk & Compliance',
    fieldKeys: [
      'risk_factors',
      'liability_risk',
    ] as const,
    options: {
      risk_factors: RISK_FACTOR_OPTIONS,
      liability_risk: LIABILITY_RISK_OPTIONS,
    },
  },
  {
    step: 7,
    title: 'Banking, Tax & Financial Setup',
    fieldKeys: [
      'has_business_bank_account',
      'uses_bookkeeping_software',
      'bookkeeping_software_name',
      'discussed_tax_planning',
    ] as const,
    options: {
      has_business_bank_account: YES_NO_OPTIONS,
      uses_bookkeeping_software: YES_NO_OPTIONS,
      discussed_tax_planning: YES_NO_OPTIONS,
    },
    conditionals: {
      bookkeeping_software_name: (data: EntityIntakeDefaults) => data.uses_bookkeeping_software === 'yes',
    },
  },
  {
    step: 8,
    title: 'Branding & Digital Presence',
    fieldKeys: [
      'owns_domain',
      'domain_name',
      'website_timeline',
      'interested_in_brand_protection',
    ] as const,
    options: {
      owns_domain: YES_NO_OPTIONS,
      website_timeline: YES_NO_LATER_OPTIONS,
      interested_in_brand_protection: YES_NO_NOT_SURE_OPTIONS,
    },
    conditionals: {
      domain_name: (data: EntityIntakeDefaults) => data.owns_domain === 'yes',
    },
  },
  {
    step: 9,
    title: 'Capital & Growth Planning',
    fieldKeys: [
      'initial_funding',
      'initial_funding_other',
      'plans_to_raise_capital',
    ] as const,
    options: {
      initial_funding: INITIAL_FUNDING_OPTIONS,
      plans_to_raise_capital: YES_NO_NOT_SURE_OPTIONS,
    },
    conditionals: {
      initial_funding_other: (data: EntityIntakeDefaults) => Array.isArray(data.initial_funding) && data.initial_funding.includes('other'),
    },
  },
  {
    step: 10,
    title: 'Additional Services Interest',
    fieldKeys: [
      'additional_services',
    ] as const,
    options: {
      additional_services: ADDITIONAL_SERVICES_OPTIONS,
    },
  },
  {
    step: 11,
    title: 'Acknowledgments',
    fieldKeys: [
      'acknowledge_not_attorney_or_cpa',
      'acknowledge_entity_formation_scope',
      'certify_accuracy',
      'client_signature',
      'signature_date',
    ] as const,
    // Note: acknowledgment fields are checkboxes (boolean), no options needed
  },
] as const;

/**
 * Helper function to check if a field should be visible based on conditional rules.
 * @param fieldKey - The field key to check visibility for
 * @param data - The form data object
 * @returns true if the field should be visible, false otherwise
 */
export function isEntityIntakeFieldVisible(
  fieldKey: EntityIntakeFieldKey,
  data: EntityIntakeDefaults
): boolean {
  // Conditional visibility rules
  switch (fieldKey) {
    case 'citizenship_status':
      return data.us_citizen === 'no';
    
    case 'bookkeeping_software_name':
      return data.uses_bookkeeping_software === 'yes';
    
    case 'domain_name':
      return data.owns_domain === 'yes';
    
    case 'industry_other':
      return Array.isArray(data.industry) && data.industry.includes('other');
    
    case 'strategic_intent_other':
      return Array.isArray(data.strategic_intent) && data.strategic_intent.includes('other');
    
    case 'initial_funding_other':
      return Array.isArray(data.initial_funding) && data.initial_funding.includes('other');
    
    default:
      // All other fields are always visible
      return true;
  }
}

/**
 * Gets the visible field keys for a specific step based on conditional rules.
 * @param step - The step number (1-11)
 * @param data - The form data object
 * @returns Array of field keys that should be visible for this step
 */
export function getVisibleFieldKeysForStep(
  step: number,
  data: EntityIntakeDefaults
): EntityIntakeFieldKey[] {
  const section = ENTITY_INTAKE_SECTIONS.find((s) => s.step === step);
  
  if (!section) {
    // Step not found, return empty array
    return [];
  }

  const fieldKeys = section.fieldKeys as readonly EntityIntakeFieldKey[];
  
  // Filter to only include visible fields
  return fieldKeys.filter((fieldKey) => isEntityIntakeFieldVisible(fieldKey, data));
}

